import React, { useState, createContext, useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import {
  get,
  groupBy,
  mapValues,
  map,
  sortBy,
  replace,
  take,
  isEmpty,
  invoke,
  find,
  toNumber,
  upperCase,
  inRange,
  filter,
  capitalize,
  has,
  cond,
  isEqual,
} from 'lodash';
import moment from 'moment';
import Checkbox from '../ScheduledItems/Checkbox';
import AppLabels from '../../utils/appLabels';
import Loader from '../../common/Loading/loader';
import UserMessage from '../../common/UserMessage/UserMessage';
import { PreferencesContext, PreferencesContextType } from '../Preferences/PreferencesContext';
import { ModalContext, ModalContextType } from '../../common/Modal/Modal';
import { Spinner } from '../../common/Spinner/Spinner';
import formatString, { formatPrice } from '../../utils/formatString';

const SlotPreferencesStyle = css`
  width: 100%;

  & .description {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.25rem;
    text-align: left;
    color: #000000;
  }

  & .banner {
    margin-bottom: 1rem;
  }
`;

const SlotColumnsContainerStyle = css`
  display: flex;
  width: 100%;
  gap: 1.5rem;
  min-width: 51rem; /* 3 14rem cols + 1rem padding left and right + 1.5rem gap */
  min-height: 19rem;
  max-height: 19rem;
  overflow: hidden auto;

  @media (max-width: 641px) {
    flex-direction: column;
    overflow-y: auto;
    gap: 0.5rem;
    -ms-overflow-style: none;
    scrollbar-width: none;
    min-width: initial;
    min-height: initial;
    max-height: initial;
  }
`;

const timeSlotHeadersRow = css`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 641px) {
    max-width: 90vw;
    overflow: auto hidden;
  }

  & > .slot_header {
    padding: 0.75rem 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
    border-radius: 0.5rem;
    border: 1px solid #7d7b78;
    background: #fff;
    cursor: pointer;
    color: #2a2928;
  }

  & > .slot_header.active {
    background: #7b1a27;
    border: 0;
    color: #fff;
  }
`;

const SlotColumnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0.5rem 1rem;
  min-width: 14rem;

  @media (max-width: 641px) {
    min-width: 50vw;
  }
`;

const SlotColumnTitleStyle = css`
  padding: 0.75rem 0;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 125%;
  overflow: hidden hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  min-height: 3rem;
  box-sizing: border-box;
`;

const MoreOptionsButtonStyle = css`
  display: flex;
  align-items: center;
  border: 0;
  background: 0;
  padding: 0.5rem 0;
  cursor: pointer;
  color: #2a2928;
`;

const SlotRowStyle = css`
  display: flex;
  padding: 0.5rem 0;
  color: #2a2928;
`;

const SlotTimeStyle = css`
  flex: 1;
`;

const buttonContainer = css`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;

  button[disabled] {
    background: #f1f0ee;
    color: #535250;
    cursor: initial;
  }

  button.saving[disabled] {
    background-color: #7b1a27;
    color: #fff;
  }
`;

const saveSlotsButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  border: 0;
  border-radius: 6.25rem;
  padding: 0.5rem 0.75rem;
  min-width: 14rem;
  cursor: pointer;
  min-height: 3.3rem;
`;

const errorContainer = css`
  width: 100%;
  padding: 1rem;
`;

const priceStyle = css`
  display: flex;
  align-items: center;
`;

const freePriceStyle = css`
  color: #437f2f;
  font-weight: 600;
`;

const radioStyle = css`
  accent-color: #7b1a27;
  width: 1.5rem;
  height: 1.5rem;
  vertical-align: middle;
  cursor: pointer;
`;

const labelStyle = css`
  margin-left: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.3125rem;
  display: flex;
  align-items: center;
  flex: 1;
`;

const pickupTabBannerSpace = css`
  margin-top: 1rem;
`;

type TimeSlot = {
  id: string;
  dayOfWeek: 'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY';
  serviceType: string;
  slotStartTime: number;
  slotEndTime: number;
  slotExpiryTime: number;
  charge: {
    deliveryCharge: number;
    minimumBasketSize: number;
    reducedDeliveryCharge: number;
  };
  status: string;
  displaySlotType: string;
  slotdisplayStart: number;
  dropTime: number;
  tillByTime: number;
  stageByTime: number;
  priceType: string[];
  pickerOption: string;
  fullFillmentVendor: string;
};

const SlotDayHeader = (props) => {
  const day = get(props, 'day');
  const active: boolean = get(props, 'active');
  const handleClick = get(props, 'onClick');

  return (
    <button
      aria-label={day}
      aria-roledescription="button"
      className={`slot_header ${active ? ' active' : ''}`}
      onClick={handleClick}
      role="tab"
      aria-selected={active}
      aria-controls="slots_tabpanel"
    >
      {AppLabels.SlotPreferences[`${day}_header`]}
    </button>
  );
};

SlotDayHeader.propTypes = {
  day: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

const SlotTimesColumn = (props) => {
  const deliveryType = get(props, 'deliveryType');
  const deliveryFeeWaived = get(props, 'deliveryFeeWaived');
  const selectedDay = get(props, 'selectedDay');
  const [expanded, setExpanded] = useState(false);
  const columnData: TimeSlot[] = sortBy(get(props, 'columnData'), 'slotStartTime');
  const { selectedSlot, setSelectedSlot } = useContext(TimeSlotContext) as TimeSlotContext;

  return (
    <div className={SlotColumnStyle}>
      <div className={SlotColumnTitleStyle}>{AppLabels.SlotPreferences[`${deliveryType}_COL_HEADER`]}</div>
      <div role={'radiogroup'} aria-label={AppLabels.SlotPreferences[`${deliveryType}_COL_HEADER`]}>
        {map(expanded ? columnData : take(columnData, 5), (slot: TimeSlot, idx) => {
          const slotStartTime: string = moment(replace(`${slot.slotStartTime}`, /(?=\d{2}$)/g, ':'), 'h:ss').format(
            'h A',
          );

          const slotEndTime: string = moment(replace(`${slot.slotEndTime}`, /(?=\d{2}$)/g, ':'), 'h:ss').format('h A');

          const label = `${slotStartTime} - ${slotEndTime}`;
          const ariaLabel = formatString(AppLabels.SlotPreferences.SLOT_TIME_ARIA_LABEL, {
            slotStartTime,
            slotEndTime,
            selectedDay,
          });

          return (
            <div className={`${SlotRowStyle} body-text-sm`} key={slot.id}>
              <input
                tabIndex={0}
                id={`radio-${deliveryType}${idx}`}
                aria-roledescription="radio button"
                aria-label={ariaLabel}
                className={radioStyle}
                type="radio"
                value={`${slotStartTime} - ${slotEndTime}`}
                checked={slot.id === selectedSlot.id}
                onChange={() => setSelectedSlot(slot)}
              />
              <label htmlFor={`radio-${deliveryType}${idx}`} className={labelStyle} aria-label={ariaLabel}>
                {label}
              </label>
              {deliveryFeeWaived ? (
                <span className={freePriceStyle}>{AppLabels.SlotPreferences.FREE_PRICE}</span>
              ) : (
                <span className={priceStyle}>{`$${formatPrice(slot.charge.deliveryCharge)}`}</span>
              )}
            </div>
          );
        })}
      </div>
      {!isEmpty(columnData) ? (
        !expanded ? (
          <div>
            <button
              className={`${MoreOptionsButtonStyle} body-text`}
              onClick={() => setExpanded(true)}
              aria-label="more options"
            >
              {AppLabels.SlotPreferences.MORE_OPTIONS}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 9.46967C18.2374 9.17678 17.7626 9.17678 17.4697 9.46967L12 14.9393L6.53033 9.46967C6.23744 9.17678 5.76256 9.17678 5.46967 9.46967C5.17678 9.76256 5.17678 10.2374 5.46967 10.5303L11.4697 16.5303C11.7626 16.8232 12.2374 16.8232 12.5303 16.5303L18.5303 10.5303C18.8232 10.2374 18.8232 9.76256 18.5303 9.46967Z"
                  fill="#2A2928"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div>
            <button
              className={`${MoreOptionsButtonStyle} body-text`}
              onClick={() => setExpanded(false)}
              aria-label="less options"
            >
              {AppLabels.SlotPreferences.LESS_OPTIONS}
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M 18.53 16.53 C 18.237 16.823 17.763 16.823 17.47 16.53 L 12 11.061 L 6.53 16.53 C 6.237 16.823 5.763 16.823 5.47 16.53 C 5.177 16.237 5.177 15.763 5.47 15.47 L 11.47 9.47 C 11.763 9.177 12.237 9.177 12.53 9.47 L 18.53 15.47 C 18.823 15.763 18.823 16.237 18.53 16.53 Z"
                  fill="#2A2928"
                ></path>
              </svg>
            </button>
          </div>
        )
      ) : null}
      {isEmpty(columnData) ? (
        <span className="body-text-sm">{AppLabels.SlotPreferences.UNAVAILABLE_PICKUP}</span>
      ) : null}
    </div>
  );
};

SlotTimesColumn.propTypes = {
  columnData: PropTypes.any.isRequired,
  deliveryType: PropTypes.oneOf(['ONEHR', 'TWOHR', 'FOURHR']).isRequired,
  deliveryFeeWaived: PropTypes.bool.isRequired,
  selectedDay: PropTypes.string,
};

type slot = {
  id?: string;
};

type TimeSlotContext = {
  selectedSlot: slot;
  setSelectedSlot: (s: slot) => void;
};
export const TimeSlotContext = createContext<TimeSlotContext | null>(null);

const SlotPreferences = (props) => {
  const currentPreferencesContext = useContext(PreferencesContext) as PreferencesContextType;
  const currentModalContext = useContext(ModalContext) as ModalContextType;
  const slotsInfo = get(currentPreferencesContext, 'slotsInfo');
  const loadingInfo: boolean = get(slotsInfo, 'loading', false);
  const infoError = get(slotsInfo, 'error', false);
  const preselectedSlot = get(props, 'preselectedSlot');
  const data = get(slotsInfo, 'data.masterSlotDetails');
  const pickup: boolean = get(props, 'pickup', false);
  const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const columnTypes = ['ONEHR', 'TWOHR', 'FOURHR'];
  const isSelectionCheckEnabled = get(props, 'isSelectionCheckEnabled', true);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState({});
  const prevSelectedSlot = useRef<object | undefined>();
  const [savingSlots, setSavingSlots] = useState(false);
  const [invalidSelection, setInvalidSelection] = useState(false);
  const [changedSelection, setChangedSelection] = useState(false);
  const [shouldCheckSelectionChange, setShouldCheckSelectionChange] = useState(false);

  const slotType = (slot) => {
    const diff: number = get(slot, 'slotEndTime', 0) - get(slot, 'slotStartTime', 0);

    return inRange(diff, 101) ? 'ONEHR' : inRange(diff, 101, 201) ? 'TWOHR' : 'FOURHR';
  };

  const slotDetails = mapValues(groupBy(data, 'dayOfWeek'), (val) => {
    const serviceType = pickup ? 'DUG' : 'DELIVERY';
    return groupBy(
      filter(val, (s) => get(s, 'serviceType') === serviceType),
      slotType,
    );
  });

  const saveSlots = async () => {
    setSavingSlots(true);

    const slotStartTime: string = moment(
      replace(`${get(selectedSlot, 'slotStartTime')}`, /(?=\d{2}$)/g, ':'),
      'H:ss',
    ).format('HH:mm:ss');

    const slotEndTime: string = moment(
      replace(`${get(selectedSlot, 'slotEndTime')}`, /(?=\d{2}$)/g, ':'),
      'H:ss',
    ).format('HH:mm:ss');

    const result = await invoke(currentPreferencesContext, 'updateSlotsInfo', {
      slotId: get(selectedSlot, 'id'),
      fulfillmentDay: selectedDay,
      slotStartTS: slotStartTime,
      slotEndTS: slotEndTime,
      slotType: slotType(selectedSlot),
    });

    if (get(result, 'error', false)) {
      invoke(currentModalContext, 'setModalError', AppLabels.SlotPreferences.LOADING_ERROR);
      analyticsEvents('save-unsuccess', false);
    } else {
      invoke(props, 'onConfirm');
      invoke(currentPreferencesContext, 'fetchPreferences');
      analyticsEvents('save-success', true, selectedSlot);
    }

    setSavingSlots(false);
  };

  const analyticsEvents = (message, success?, ARObject?) => {
    try {
      if (window['AB']?.DATALAYER) {
        if (window?.['digitalData']?.pageFlags?.isChangeDateTime) {
          if (success) {
            const autoReplenishmentObj = window['AB'].DATALAYER.setDeliveryWindowAR(ARObject, 'newSNSPage');
            window['AB'].DATALAYER.setModalClickEventsAR(`ar:change-date-time|${message}`, autoReplenishmentObj);
          } else if (!success) {
            window['AB'].DATALAYER.setModalClickEvents(`ar:change-date-time|${message}`);
          }
        } else {
          if (success && window['digitalData']?.modal) {
            window['digitalData'].modal.modalMessage = window['digitalData'].pageFlags?.preferences
              ? `change-in-${window['digitalData'].pageFlags.preferences}-fullfillment`
              : '';
            window['AB'].DATALAYER.setModalClickEvents(
              `ar:fulfillment-edit-${window['digitalData'].pageFlags?.preferences}|date-time-save`,
            );
          }
          window['digitalData'].modal.modalMessage = '';
        }
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  useEffect(() => {
    invoke(currentModalContext, 'setScrollable', true);
    invoke(currentPreferencesContext, 'fetchSlotsInfo');
  }, []);

  useEffect(() => {
    if (shouldCheckSelectionChange) {
      if (has(prevSelectedSlot, 'current.dayOfWeek') && !isEqual(get(prevSelectedSlot, 'current'), selectedSlot)) {
        setChangedSelection(true);
      }
      prevSelectedSlot.current = selectedSlot;
    }
  }, [selectedSlot, shouldCheckSelectionChange]);

  useEffect(() => {
    setShouldCheckSelectionChange(isSelectionCheckEnabled);
    if (!isSelectionCheckEnabled) {
      setChangedSelection(false);
    }
  }, [isSelectionCheckEnabled]);

  useEffect(() => {
    const preselected = find(data, {
      dayOfWeek: upperCase(get(preselectedSlot, 'fulfillmentDay')),
      slotStartTime: toNumber(moment(get(preselectedSlot, 'slotStartTS'), 'H:mm A').format('Hmm')),
      slotEndTime: toNumber(moment(get(preselectedSlot, 'slotEndTS'), 'H:mm A').format('Hmm')),
      serviceType: pickup ? 'DUG' : 'DELIVERY',
    });

    if (preselected) {
      setSelectedDay(get(preselected, 'dayOfWeek'));
      setSelectedSlot(preselected);
    } else {
      const today = moment().format('dddd').toUpperCase();
      setSelectedDay(today);
    }
  }, [data]);

  useEffect(() => {
    // make sure that the selected slot is the right type, disable the save button if it isn't
    if (pickup && get(selectedSlot, 'serviceType') !== 'DUG') {
      setInvalidSelection(true);
    } else if (!pickup && get(selectedSlot, 'serviceType') !== 'DELIVERY') {
      setInvalidSelection(true);
    } else if (selectedDay !== get(selectedSlot, 'dayOfWeek')) {
      // also if selected day doesn't match currently selected slot's day
      setInvalidSelection(true);
    } else {
      setInvalidSelection(false);
    }
  }, [selectedSlot, selectedDay]);

  useEffect(() => {
    if (infoError) {
      invoke(currentModalContext, 'setModalError', AppLabels.SlotPreferences.LOADING_ERROR);
    }
  }, [infoError]);

  const banner = cond([
    [
      () => get(slotsInfo, 'data.bannerMessage') === 'CREATED',
      () => {
        return (
          <div className={`banner ${pickup ? pickupTabBannerSpace : ''}`}>
            <UserMessage
              messageType="info"
              messageText={formatString(AppLabels.SlotPreferences.CREATED_INFO, {
                weekDay: moment(get(slotsInfo, 'data.nextOrderDate'), 'YYYY-M-D').format('ddd'),
                date: moment(get(slotsInfo, 'data.nextOrderDate'), 'YYYY-M-D').format('MMM D'),
              })}
            />
          </div>
        );
      },
    ],
    [
      () => get(slotsInfo, 'data.bannerMessage') === 'PREPARING' && (isSelectionCheckEnabled ? changedSelection : true),
      () => {
        return (
          <div className={`banner ${pickup ? pickupTabBannerSpace : ''}`}>
            <UserMessage messageType="warning" messageText={AppLabels.SlotPreferences.PREPARING_WARNING} />
          </div>
        );
      },
    ],
    [
      () => true,
      () => {
        return null;
      },
    ],
  ]);

  return (
    <div className={`item-list-container ${SlotPreferencesStyle}`}>
      <TimeSlotContext.Provider value={{ selectedSlot, setSelectedSlot }}>
        {banner()}
        <div className={`description ${pickup ? pickupTabBannerSpace : ''}`}>
          <p>{AppLabels.SlotPreferences.SLOTS_DESCRIPTION}</p>
        </div>
        <div className={timeSlotHeadersRow} role="tablist">
          {days.map((d) => (
            <SlotDayHeader key={d} day={d} active={selectedDay === d} onClick={() => setSelectedDay(d)} />
          ))}
        </div>
        <div className={`item-list-container ${SlotColumnsContainerStyle}`} id="slots_tabpanel" role="tabpanel">
          {loadingInfo ? (
            <Loader></Loader>
          ) : !data ? (
            <div className={errorContainer}>
              <UserMessage messageType="warning" messageText={AppLabels.SlotPreferences.LOADING_ERROR} />
            </div>
          ) : (
            map(columnTypes, (type, i) => {
              return (
                <SlotTimesColumn
                  key={i}
                  deliveryType={type}
                  columnData={get(slotDetails, `${selectedDay}.${type}`, [])}
                  deliveryFeeWaived={get(slotsInfo, 'data.deliveryFeeWaived', false)}
                  selectedDay={capitalize(selectedDay)}
                />
              );
            })
          )}
        </div>
      </TimeSlotContext.Provider>

      {!loadingInfo ? (
        <div className={buttonContainer}>
          <button
            className={`${saveSlotsButtonStyle} ${savingSlots ? 'saving' : ''}`}
            onClick={saveSlots}
            disabled={(isSelectionCheckEnabled && !changedSelection) || savingSlots || invalidSelection}
            aria-label="save slot preferences"
          >
            {savingSlots ? <Spinner /> : <span className="body-text">Save</span>}
          </button>
        </div>
      ) : null}
    </div>
  );
};

SlotPreferences.propTypes = {
  pickup: PropTypes.bool,
  onConfirm: PropTypes.func,
  preselectedSlot: PropTypes.object,
  isSelectionCheckEnabled: PropTypes.bool,
};

export default SlotPreferences;
